import React from 'react';
import LegalPage from './LegalPage';

const PrivacyPolicy = () => (
  <LegalPage title="Privacy Policy" updated="25 April 2026">
    <p>
      This Privacy Policy explains how <strong>RT Connect Technology Pvt Ltd</strong>{' '}
      ("we", "us", or "RT Connect") handles information collected through the
      EduManage platform — including the web application at{' '}
      <a href="https://edu.rtconnect.in">edu.rtconnect.in</a> and the EduManage
      mobile app on Google Play and the Apple App Store.
    </p>

    <h2>1. Who this applies to</h2>
    <p>
      EduManage is a business-to-business platform sold to schools. The school
      is the data controller for student, parent, teacher, and admin records.
      RT Connect acts as the processor under the school's instructions. This
      policy covers what we, as the processor, do with that information.
    </p>

    <h2>2. Information we collect</h2>

    <h3>Account information</h3>
    <ul>
      <li>Full name (first and last)</li>
      <li>Email address</li>
      <li>Password (stored as a bcrypt hash — we never see the plain text)</li>
      <li>Role (admin, teacher, parent, student, or platform owner)</li>
      <li>Phone number (optional)</li>
      <li>School association (the tenant your account belongs to)</li>
    </ul>

    <h3>School records</h3>
    <p>
      Records created by school admins and teachers in the normal course of
      running a school:
    </p>
    <ul>
      <li>Student rolls: admission number, class, section, date of birth, gender, guardian names, contact phone and email, address</li>
      <li>Attendance records (date, status, teacher who marked)</li>
      <li>Marks and grade entries (exam name, subject, marks obtained)</li>
      <li>Homework assignments and student diary entries</li>
      <li>Health records, achievements, leave records (where the school uses those modules)</li>
      <li>Announcements, circulars, timetable entries</li>
    </ul>

    <h3>Mobile device information</h3>
    <ul>
      <li>
        <strong>Push notification token</strong> — issued by Apple or Google, used
        only to deliver notifications you explicitly opt into (announcement,
        circular, homework, mark posted). You can revoke at the OS level at any
        time.
      </li>
      <li>
        <strong>Device type and OS version</strong> — logged with API requests for
        diagnostic purposes.
      </li>
    </ul>

    <h3>Information we do NOT collect</h3>
    <ul>
      <li>Location or GPS data</li>
      <li>Photos, contacts, or anything from your device outside the app</li>
      <li>Advertising identifiers</li>
      <li>Third-party analytics or marketing trackers</li>
      <li>Payment card details — schools pay us via invoice/bank transfer outside the app</li>
    </ul>

    <h2>3. How we use information</h2>
    <ul>
      <li>To provide the service to your school (show your child's attendance, deliver a teacher's homework post, etc.)</li>
      <li>To authenticate you at login</li>
      <li>To send push notifications you have opted into</li>
      <li>To diagnose bugs and improve reliability (server logs — auto-rotated)</li>
      <li>To comply with Indian tax and company law where applicable</li>
    </ul>
    <p>
      We do not sell or share personal information with advertisers. We do not
      use your school's data to train models or for any purpose outside running
      the service for your school.
    </p>

    <h2>4. Children's privacy</h2>
    <p>
      EduManage is deployed in K–12 schools, so data about students under 13
      routinely flows through it. We treat this data in line with India's Digital
      Personal Data Protection Act (DPDP 2023) and, where applicable, the US
      Children's Online Privacy Protection Act (COPPA) and the EU's GDPR-K.
    </p>
    <ul>
      <li>Students under 13 do not self-register. Their accounts (where they exist at all) are created by their school admin.</li>
      <li>Most young students are accessed via their parent's login, not their own.</li>
      <li>Parents and guardians can request a copy or deletion of their child's records at any time — see Section 8.</li>
      <li>We do not show advertising to any user, including children.</li>
    </ul>

    <h2>5. Who we share data with</h2>
    <p>
      We use the following infrastructure providers, all of which are contractually
      obligated to protect your data:
    </p>
    <ul>
      <li><strong>Vercel</strong> — web and API hosting</li>
      <li><strong>Neon</strong> — managed Postgres database hosting (Singapore region)</li>
    </ul>
    <p>
      We do not share data with advertisers, data brokers, or any other third
      party for commercial purposes. We disclose information only when required
      by a lawful order from an Indian court or regulator.
    </p>

    <h2>6. How we protect data</h2>
    <ul>
      <li>All traffic is encrypted in transit with TLS 1.2 or higher</li>
      <li>Passwords are hashed with bcrypt (cost factor 12)</li>
      <li>Each school's data is isolated via per-row tenant scoping at the database layer</li>
      <li>Parent accounts can only view records for their own children — enforced in both the frontend and the API</li>
      <li>Admin access is role-gated; a teacher cannot view the whole school's records, a parent cannot view other children's records</li>
      <li>Server logs are retained for 30 days then automatically purged</li>
    </ul>

    <h2>7. Data retention</h2>
    <p>
      We retain school records for the duration of the school's active
      subscription. When a school cancels, we retain the data for 90 days to
      allow restoration if they resubscribe, then permanently delete it. A
      school can request immediate deletion at any time.
    </p>

    <h2>8. Your rights</h2>
    <p>You can request any of the following by emailing{' '}
      <a href="mailto:pramod@rtconnect.in">pramod@rtconnect.in</a>:
    </p>
    <ul>
      <li>A copy of the data we hold about you (or your child)</li>
      <li>Correction of inaccurate records</li>
      <li>Deletion of your account and personal data — see our{' '}
        <a href="/data-deletion">Data Deletion</a> page for the dedicated flow</li>
      <li>Withdrawal of consent for push notifications (or disable them in OS settings)</li>
    </ul>
    <p>
      We respond to verified requests within 30 days. For student records, we
      verify the request is coming from the parent/guardian on file or the
      school's admin.
    </p>

    <h2>9. Changes to this policy</h2>
    <p>
      We may update this policy as the product evolves. Material changes will
      be announced inside the app and via email to school admins at least 14
      days before they take effect. The "Last updated" date above always
      reflects the current version.
    </p>

    <h2>10. Contact</h2>
    <p>
      RT Connect Technology Pvt Ltd<br />
      Email: <a href="mailto:pramod@rtconnect.in">pramod@rtconnect.in</a><br />
      Web: <a href="https://rtconnect.in">rtconnect.in</a>
    </p>
  </LegalPage>
);

export default PrivacyPolicy;
